import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Fab,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  IconButton
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Build as BuildIcon,
  Assignment as TaskIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { car as carApi } from '../../utils/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../components/auth/AuthModule';
import { format } from 'date-fns';
import { getTransformedImageUrl } from '../../utils/filestack';

const CarsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['cars', 'common']);
  const { user } = useAuth();
  
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    year: ''
  });
  const [saving, setSaving] = useState(false);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchCars();
  }, [tabValue]);

  const fetchCars = async () => {
    try {
      setLoading(true);
      let carsData;
      
      if (tabValue === 0) {
        // My Cars
        carsData = await carApi.getUserCars();
      } else {
        // All Cars
        carsData = await carApi.getAllCars();
      }
      
      setCars(carsData || []);
    } catch (error) {
      console.error('Error fetching cars:', error);
      setError(t('errors.fetchingCars'));
      toast.error(t('errors.fetchingCars'));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleCreateCar = async () => {
    try {
      setSaving(true);
      await carApi.create(formData);
      toast.success(t('success.carCreated'));
      setCreateDialogOpen(false);
      setFormData({
        name: '',
        model: '',
        year: ''
      });
      fetchCars();
    } catch (error) {
      console.error('Error creating car:', error);
      toast.error(t('errors.creatingCar'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={4}>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('cars.addCar')}
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={t('cars.myCars')} />
            <Tab label={t('cars.allCars')} />
          </Tabs>
        </Box>

        {error ? (
          <Typography color="error">{error}</Typography>
        ) : cars.length > 0 ? (
          <Grid container spacing={3}>
            {cars.map((car) => (
              <Grid item xs={12} sm={6} md={4} key={car._id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                >
                  {/* Car Image */}
                  <Box 
                    sx={{ 
                      height: 160, 
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      '&:hover': {
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          backgroundColor: 'rgba(0, 0, 0, 0.1)',
                          zIndex: 1
                        }
                      }
                    }}
                    onClick={() => navigate(`/cars/${car._id}`)}
                  >
                    {!imageErrors[car._id] && car.imageUrl ? (
                      <img 
                        src={getTransformedImageUrl(car.imageUrl, 'app')} 
                        alt={car.name}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onError={() => {
                          setImageErrors(prev => ({
                            ...prev,
                            [car._id]: true
                          }));
                        }}
                      />
                    ) : (
                      <Box 
                        sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '100%',
                          height: '100%',
                          color: 'text.secondary',
                          cursor: 'pointer'
                        }}
                      >
                        <CarIcon sx={{ fontSize: 60 }} />
                        <Typography variant="caption" sx={{ mt: 1 }}>
                          {car.model || car.name}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Task Badge - Show if car has tasks */}
                    {car.taskCount > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          bgcolor: 'primary.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: 36,
                          height: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 2,
                          boxShadow: 2
                        }}
                      >
                        <TaskIcon fontSize="small" />
                      </Box>
                    )}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box display="flex" alignItems="center" mb={2}>
                      <CarIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6" component="h2">
                        {car.name}
                      </Typography>
                    </Box>
                    
                    
                    
                    {tabValue === 1 && car.user && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {car.user.name || car.user.username}
                      </Typography>
                    )}
                    
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<BuildIcon />}
                      onClick={() => navigate(`/cars/${car._id}`)}
                    >
                      {t('carDetail.details')}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box textAlign="center" py={6}>
            <CarIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t('cars.noCars')}
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 2 }}
            >
              {t('cars.addFirstCar')}
            </Button>
          </Box>
        )}
      </Box>

      {/* Create Car Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogTitle>{t('createCar.title')}</DialogTitle>
        <DialogContent>
          <Box pt={1}>
            <TextField
              name="name"
              label={t('createCar.fields.name')}
              fullWidth
              value={formData.name}
              onChange={handleFormChange}
              margin="normal"
              required
            />
            <TextField
              name="model"
              label={t('createCar.fields.model')}
              fullWidth
              value={formData.model}
              onChange={handleFormChange}
              margin="normal"
            />
            <TextField
              name="year"
              label={t('createCar.fields.year')}
              fullWidth
              value={formData.year}
              onChange={handleFormChange}
              margin="normal"
              type="number"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleCreateCar} 
            variant="contained" 
            color="primary"
            disabled={saving || !formData.name}
          >
            {saving ? <CircularProgress size={24} /> : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CarsPage; 